// DateTime.js

import { Timestamp } from 'firebase/firestore';
import { isToday, format } from 'date-fns';

export const dayNamesLetter = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
export const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const dayNamesLong = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const getLocalTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/**
 * Formats a timestamp into a human-readable relative time string.
 * Handles both past and future timestamps.
 *
 * @param {Timestamp} timestamp - The Firebase timestamp to format.
 * @returns {string} A formatted relative time string (e.g., "2 days ago" or "in 3 hours").
 */
export function getRelativeTime(timestamp) {
  const now = new Date();
  const nodeDate = typeof timestamp?.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
  const diffInMs = nodeDate - now; // Positive for future, negative for past

  const minutes = Math.floor(Math.abs(diffInMs) / 60000);
  const hours = Math.floor(Math.abs(diffInMs) / 3600000);
  const days = Math.floor(Math.abs(diffInMs) / 86400000);
  const months = Math.floor(Math.abs(diffInMs) / 2592000000); // Approximate month
  const years = Math.floor(Math.abs(diffInMs) / 31536000000); // Approximate year

  const isFuture = diffInMs > 0;
  const timeUnit = (value, unit) => `${value} ${unit}${value > 1 ? 's' : ''}`;

  if (years > 0) return isFuture ? `in ${timeUnit(years, 'year')}` : `${timeUnit(years, 'year')} ago`;
  if (months > 0) return isFuture ? `in ${timeUnit(months, 'month')}` : `${timeUnit(months, 'month')} ago`;
  if (days > 0) return isFuture ? `in ${timeUnit(days, 'day')}` : `${timeUnit(days, 'day')} ago`;
  if (hours > 0) return isFuture ? `in ${timeUnit(hours, 'hour')}` : `${timeUnit(hours, 'hour')} ago`;
  if (minutes > 0) return isFuture ? `in ${timeUnit(minutes, 'minute')}` : `${timeUnit(minutes, 'minute')} ago`;
  return isFuture ? 'Soon' : 'Just now';
}

export function getRelativeTimeShort(timestamp) {
  const now = new Date();
  const commentDate = typeof timestamp?.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
  const diffInMs = now - commentDate;

  const seconds = Math.floor(diffInMs / 1000);
  const minutes = Math.floor(diffInMs / 60000);
  const hours = Math.floor(diffInMs / 3600000);
  const days = Math.floor(diffInMs / 86400000);
  const weeks = Math.floor(diffInMs / 604800000); // Approximate week
  const months = Math.floor(diffInMs / 2592000000); // Approximate month
  const years = Math.floor(diffInMs / 31536000000); // Approximate year

  if (years > 0) return `${years}y`;
  if (months > 0) return `${months}mo`;
  if (weeks > 0) return `${weeks}w`;
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  if (seconds > 0) return `${seconds}s`;
  return 'Just now';
};


export function getMonthAndYearString(year, month) {
  return new Date(year, month).toLocaleString('default', { month: 'long' }) + " " + year
};

export function getMonthDays(year, month) {
  return new Date(year, month + 1, 0).getDate();
};

export function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
};


/**
 * Calculates the number of days in a given month and year.
 * 
 * @param {number} month The month.
 * @param {number} year The year.
 * 
 * @returns {Array} The days in the month.
 */
export function getDaysInMonth(monthIndex, year) {
  const daysInMonth = new Date(year, monthIndex, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => ({ value: `${i + 1}`, label: `${i + 1}` }));
}

/**
* Checks if a given date is in the future.
* Accepts Firestore Timestamp, Date, or ISO string.
*
* @param {any} date - The date object to check.
* @returns {boolean} True if the date is valid and in the future.
*/
export function isFutureDate(date) {
  if (!date) return false;

  const d = date?.toDate ? date.toDate() : new Date(date);
  if (isNaN(d.getTime())) return false;

  return d.getTime() > Date.now();
}

export function getCalendarHeaderLabels(date) {
  if (isToday(date)) {
    return { day: 'Today', number: format(date, 'd') };
  }
  return {
    month: format(date, 'MMM'),    // e.g. Sep
    dayNumber: format(date, 'd'),  // e.g. 21
    day: format(date, 'EEE'),      // e.g. Mon
  };
}

export const normalizeDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') {
    const result = value.toDate();
    return isNaN(result?.getTime?.()) ? null : result;
  }

  if (typeof value?.toMillis === 'function') {
    const result = new Date(value.toMillis());
    return isNaN(result.getTime()) ? null : result;
  }

  if (
    typeof value === 'object'
    && (
      typeof value?.seconds === 'number'
      || typeof value?._seconds === 'number'
    )
  ) {
    const result = new Date(timestampToMillis(value));
    return isNaN(result.getTime()) ? null : result;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Whether a precision flag means date-only (all-day / civil calendar day).
 *
 * @param {string|null|undefined} precision
 * @returns {boolean}
 */
export const isDatePrecision = (precision) => {
  const normalized = String(precision || '').trim().toLowerCase();
  return !normalized || normalized === 'date';
};

/**
 * True when value already looks like an Appdeck datetimerange object.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export const isDateTimeRangeValue = (value) => (
  Boolean(
    value
    && typeof value === 'object'
    && !Array.isArray(value)
    && (
      Object.prototype.hasOwnProperty.call(value, 'startDate')
      || Object.prototype.hasOwnProperty.call(value, 'startPrecision')
      || Object.prototype.hasOwnProperty.call(value, 'isRange')
    )
  )
);

/**
 * Coerce legacy scalar date/datetime values into Appdeck datetimerange shape.
 * Keeps Timestamp-like startDate when present so Firestore round-trips cleanly.
 *
 * @param {unknown} value
 * @returns {{
 *   startDate: *,
 *   startPrecision: 'date'|'time',
 *   endDate: *|null,
 *   endPrecision: 'date'|'time'|null,
 *   isRange: boolean,
 *   recurrence?: *,
 * }|null}
 */
export const coerceToDateTimeRangeValue = (value) => {
  if (value == null || value === '') return null;

  if (isDateTimeRangeValue(value)) {
    const startPrecision = String(value.startPrecision || '').trim().toLowerCase() === 'date'
      ? 'date'
      : 'time';
    const endDate = value.endDate ?? null;
    const endPrecision = endDate
      ? (String(value.endPrecision || value.startPrecision || '').trim().toLowerCase() === 'date'
        ? 'date'
        : 'time')
      : null;
    const next = {
      startDate: value.startDate ?? null,
      startPrecision,
      endDate,
      endPrecision,
      isRange: Boolean(value.isRange ?? endDate),
    };
    if (Object.prototype.hasOwnProperty.call(value, 'recurrence')) {
      next.recurrence = value.recurrence;
    }
    return next;
  }

  const start = normalizeDateValue(value);
  if (!start) return null;

  const keepAsTimestamp = typeof value?.toDate === 'function'
    || (
      typeof value === 'object'
      && !Array.isArray(value)
      && (
        typeof value?.seconds === 'number'
        || typeof value?._seconds === 'number'
      )
    );

  return {
    startDate: keepAsTimestamp ? value : timestampFromDate(start),
    startPrecision: isUtcDateOnlyInstant(start) ? 'date' : 'time',
    endDate: null,
    endPrecision: null,
    isRange: false,
  };
};

/**
 * Google all-day and other date-only fields are often stored as UTC midnight
 * (or noon) of that civil day. Detect that encoding so local TZ does not shift
 * the calendar day backward (e.g. Fri 00:00Z → Thu 6pm MDT).
 *
 * @param {Date} date
 * @returns {boolean}
 */
export const isUtcDateOnlyInstant = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  if (
    date.getUTCMinutes() !== 0
    || date.getUTCSeconds() !== 0
    || date.getUTCMilliseconds() !== 0
  ) {
    return false;
  }
  const hours = date.getUTCHours();
  return hours === 0 || hours === 12;
};

/**
 * Civil Y/M/D for a date-precision value.
 *
 * @param {any} value
 * @returns {{ year: number, month: number, day: number }|null}
 */
export const getDatePrecisionParts = (value) => {
  const date = normalizeDateValue(value);
  if (!date) return null;

  if (isUtcDateOnlyInstant(date)) {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth(),
      day: date.getUTCDate(),
    };
  }

  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
  };
};

/**
 * Local midnight Date for the civil day of a date-precision timestamp.
 * Use for calendar grids, DatePickers, and same-day checks.
 *
 * @param {any} value
 * @returns {Date|null}
 */
export const toDatePrecisionLocalDay = (value) => {
  const parts = getDatePrecisionParts(value);
  if (!parts) return null;
  const local = new Date(parts.year, parts.month, parts.day);
  return Number.isNaN(local.getTime()) ? null : local;
};

/**
 * Formats a node summary date consistently across summaries.
 * Accepts Firestore Timestamp, Date, ISO string, or null.
 *
 * @param {Timestamp|Date|string|null} value
 * @param {object} [options]
 * @param {boolean} [options.includeTime=true] Include hours/minutes in output.
 * @param {string|undefined} [options.locale] Optional locale override.
 * @returns {string}
 */
export const formatSummaryDate = (value, options = {}) => {
  const date = normalizeDateValue(value);
  if (!date) return '';

  const {
    includeTime = true,
    locale,
  } = options;

  const dateOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  if (includeTime) {
    dateOptions.hour = 'numeric';
    dateOptions.minute = '2-digit';
  }

  return date.toLocaleDateString(locale, dateOptions);
};

export const formatFutureDiff = (ms) => {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));

  if (totalMinutes >= 1440) {
    const days = Math.floor(totalMinutes / 1440);
    return `Starting in ${days}d`;
  }

  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const parts = [`${hours}h`];
    if (minutes > 0) parts.push(`${minutes}m`);
    return `Starting in ${parts.join(' ')}`;
  }

  if (totalMinutes <= 1) {
    return 'Starting in 1m';
  }

  return `Starting in ${totalMinutes}m`;
};

export const formatPastDiff = (ms) => {
  const totalMinutes = Math.max(1, Math.floor(ms / 60000));
  if (totalMinutes < 60) {
    return `Ended ${totalMinutes}m ago`;
  }

  const hours = Math.floor(totalMinutes / 60);
  if (hours < 24) {
    return `Ended ${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  return `Ended ${days}d ago`;
};

const hasTimePrecision = (precision, date) => {
  if (precision === 'time') return true;
  // Explicit date precision is all-day — do not infer time from local clock
  // (UTC midnight Friday is 6pm Thursday in Mountain time).
  if (precision === 'date') return false;
  if (!date) return false;
  if (!precision) {
    return date.getHours() !== 0 || date.getMinutes() !== 0;
  }
  return false;
};

const formatDatePart = (date, includeYear = false) =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {}),
  });

const formatTimePart = (date) =>
  date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

export const formatUpcomingEventWindow = (dataEvent, { now = new Date() } = {}) => {
  if (!dataEvent) return null;

  const start = normalizeDateValue(dataEvent.startDate);
  if (!start) return null;

  const end = normalizeDateValue(dataEvent.endDate);
  const includeYearStart = start.getFullYear() !== now.getFullYear();
  const includeYearEnd = end
    ? end.getFullYear() !== now.getFullYear() || end.getFullYear() !== start.getFullYear()
    : false;

  const startHasTime = hasTimePrecision(dataEvent.startPrecision || 'date', start);
  const endHasTime = hasTimePrecision(
    dataEvent.endPrecision || dataEvent.startPrecision || 'date',
    end,
  );

  const dateLabel = formatDatePart(start, includeYearStart);
  const startTimeLabel = startHasTime ? formatTimePart(start) : null;

  if (dataEvent.isRange && end) {
    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();

    if (sameDay) {
      const endTimeLabel = endHasTime ? formatTimePart(end) : null;

      if (startTimeLabel && endTimeLabel) {
        if (startTimeLabel === endTimeLabel) {
          return `${dateLabel} • ${startTimeLabel}`;
        }
        return `${dateLabel} • ${startTimeLabel} – ${endTimeLabel}`;
      }

      if (startTimeLabel || endTimeLabel) {
        return `${dateLabel} • ${startTimeLabel || endTimeLabel}`;
      }

      return dateLabel;
    }

    const startSegment = startTimeLabel ? `${dateLabel} ${startTimeLabel}` : dateLabel;
    const endDateLabel = formatDatePart(end, includeYearEnd);
    const endTimeLabel = endHasTime ? formatTimePart(end) : null;
    const endSegment = endTimeLabel ? `${endDateLabel} ${endTimeLabel}` : endDateLabel;
    return `${startSegment} – ${endSegment}`;
  }

  if (startTimeLabel) {
    return `${dateLabel} • ${startTimeLabel}`;
  }

  return dateLabel;
};

export const getEventStatusLabel = (
  dataEvent,
  { now = new Date(), liveWindowMinutes = 60 } = {},
) => {
  if (!dataEvent) return null;

  const start = normalizeDateValue(dataEvent.startDate);
  if (!start) return null;

  const diffMs = start.getTime() - now.getTime();

  if (diffMs > 0) {
    const upcomingLabel = formatUpcomingEventWindow(dataEvent, { now });
    return upcomingLabel || formatFutureDiff(diffMs);
  }

  const elapsedMs = Math.abs(diffMs);
  if (elapsedMs <= liveWindowMinutes * 60 * 1000) {
    return 'LIVE NOW';
  }

  return formatPastDiff(elapsedMs);
};

/**
 * Formats a date into a readable string.
 *
 * Supports:
 * - formatDateTime(date)
 * - formatDateTime(date, 'America/New_York')
 * - formatDateTime(date, { timeZone, locale, intlOptions })
 */
export const formatDateTime = (date, optionsOrTimeZone) => {
  if (!date) return 'Unknown';

  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Unknown';

  const defaultIntlOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const opts = typeof optionsOrTimeZone === 'string'
    ? { timeZone: optionsOrTimeZone }
    : (optionsOrTimeZone || {});

  const locale = opts?.locale || 'en-US';
  const intlOptions = opts?.intlOptions || defaultIntlOptions;
  const withTimeZone = opts?.timeZone
    ? { ...intlOptions, timeZone: opts.timeZone }
    : intlOptions;

  return d.toLocaleString(locale, withTimeZone);
};

/**
 * Converts a Firestore Timestamp or similar object to milliseconds.
 *
 * @param {any} value - The value to convert.
 * 
 * @returns {number} The milliseconds since epoch.
 */
export const timestampToMillis = (value) => {
	if (!value) return 0;
	if (typeof value.toMillis === 'function') return value.toMillis();
	if (typeof value === 'number') return value;
	if (typeof value.seconds === 'number') {
		return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
	}
	if (typeof value._seconds === 'number') {
		return value._seconds * 1000 + Math.floor((value._nanoseconds || 0) / 1e6);
	}
	return 0;
};

/**
 * Converts a Date object to a Firestore Timestamp.
 *
 * @param {Date} date - The Date object to convert.
 * 
 * @returns {Timestamp} The corresponding Firestore Timestamp.
 */
export function timestampFromDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    throw new Error('timestampFromDate: valid Date is required');
  }
  return Timestamp.fromDate(date);
}

/** * Converts milliseconds since epoch to a Firestore Timestamp.
 *
 * @param {number} milliseconds - The milliseconds since epoch.
 * 
 * @returns {Timestamp} The corresponding Firestore Timestamp.
 */
export function timestampFromMillis(milliseconds) {
  const value = Number(milliseconds);
  if (!Number.isFinite(value)) {
    throw new Error('timestampFromMillis: finite milliseconds value is required');
  }
  return Timestamp.fromMillis(value);
}


